import React, { useEffect, useState } from 'react';
import { Chart } from 'primereact/chart';
import { useTranslation } from 'react-i18next';

import ChartDataLabels from 'chartjs-plugin-datalabels';
import { totalizer } from '../../../../helpers/common';
import i18n from '../../../../config/i18n';

const formatDateLabel = (date, periodType) => {
  const options = {
    daily: { month: 'short', day: 'numeric' },
    weekly: { month: 'short', day: 'numeric' },
    monthly: { month: 'short', year: 'numeric' },
    quarterly: { month: 'short', year: 'numeric' },
  };

  const formatter = new Intl.DateTimeFormat(
    'en-US',
    options[periodType] || options.daily,
  );
  let formatted = formatter.format(date);

  // Add quarter info for quarterly
  if (periodType === 'quarterly') {
    const quarter = Math.ceil((date.getMonth() + 1) / 3);
    formatted = `Q${quarter} ${date.getFullYear()}`;
  }

  return formatted;
};

const generateTimeseriesLabels = (dateFrom, dateTo, periodType) => {
  const labels = [];
  let current = new Date(dateFrom);
  const end = new Date(dateTo);

  while (current <= end) {
    labels.push(formatDateLabel(current, periodType));

    switch (periodType) {
      case 'daily':
        current.setDate(current.getDate() + 1);
        break;
      case 'weekly':
        current.setDate(current.getDate() + 7);
        break;
      case 'monthly':
        current.setMonth(current.getMonth() + 1);
        break;
      case 'quarterly':
        current.setMonth(current.getMonth() + 3);
        break;
      default:
        current.setDate(current.getDate() + 1);
    }
  }

  return labels;
};

export const DashboardChart = ({
  title,
  labels,
  dataset,
  showLegend = true,
  indexAxis = 'x',
  borderRadius = 0,
  isTimeseries = false,
  dateFrom = null,
  dateTo = null,
  periodType = 'daily',
  chartType = null, // Type: 'sales-amount', 'sales-count', 'purchase-amount', 'purchase-count', 'vat-details', 'third-party-count', 'third-party-relationship', 'e-invoice-status'
  vatData = null, // For VAT Details charts to show all three values
}) => {
  const [chartData, setChartData] = useState({});
  const [chartOptions, setChartOptions] = useState({});
  const { t } = useTranslation();

  useEffect(() => {
    const documentStyle = window.getComputedStyle(document.documentElement);
    const textColor = documentStyle.getPropertyValue('--text-color');
    const textColorSecondary = 'black';
    const surfaceBorder = documentStyle.getPropertyValue('--surface-border');

    // Generate timeseries labels if needed
    const chartLabels =
      isTimeseries && dateFrom && dateTo
        ? generateTimeseriesLabels(dateFrom, dateTo, periodType)
        : labels;

    const data = {
      labels: chartLabels,
      datasets: dataset?.map((item, i) => {
        let datatablesConfig = {
          display: false,
        };
        if (i === dataset.length - 1) {
          datatablesConfig = {
            color: 'black',
            formatter: () => '',
            labels: {
              total: {
                offset: -30,
                anchor: 'end',
                align: 'start',
                formatter: function (value, ctx) {
                  const dataIndex = ctx.dataIndex;
                  const total = ctx.chart.data.datasets.reduce(
                    (sum, datasetInt) => sum + datasetInt.data[dataIndex],
                    0,
                  );
                  return total ? total.toLocaleString('en-US') : '';
                },
              },
            },
          };
        }
        return {
          ...item,
          barPercentage: 0.3,
          datalabels: datatablesConfig,
        };
      }),
    };

    const footer = (tooltipItems) => {
      // Calculate total for the footer
      let sum = 0;
      tooltipItems.forEach(function (tooltipItem) {
        sum += tooltipItem.parsed.y;
      });

      // For VAT Details charts, show all three VAT values
      if (chartType === 'vat-details' && vatData) {
        const dataIndex = tooltipItems[0]?.dataIndex;
        if (dataIndex !== undefined) {
          const outputVat = vatData.outputVat?.[dataIndex] || 0;
          const inputVat = vatData.inputVat?.[dataIndex] || 0;
          const netVat = vatData.netVat?.[dataIndex] || 0;

          return `${t(
            'invoice_view_page.dashboard.output-vat',
          )}: ${outputVat.toLocaleString()}\n${t(
            'invoice_view_page.dashboard.input-vat',
          )}: ${inputVat.toLocaleString()}\n${t(
            'invoice_view_page.dashboard.net-vat',
          )}: ${netVat.toLocaleString()}`;
        }
      }

      // For amount charts (sales/purchase), show Gross Total Amount
      if (chartType === 'sales-amount' || chartType === 'purchase-amount') {
        return `${t(
          'invoice_view_page.dashboard.gross-total-including-vat',
        )}: ${sum.toLocaleString()}`;
      }

      // For count charts (invoices), show Total number
      if (
        chartType === 'sales-count' ||
        chartType === 'purchase-count' ||
        chartType === 'third-party-count' ||
        chartType === 'e-invoice-status'
      ) {
        return `${t(
          'invoice_view_page.dashboard.total-number-of-e-invoices',
        )}: ${sum.toLocaleString()}`;
      }

      return '';
    };

    const afterLabel = (tooltipItem) => {
      const value = tooltipItem.formattedValue;
      const label = tooltipItem.dataset.label;

      // For E-Invoice Status charts, show number and percentage
      if (chartType === 'e-invoice-status') {
        const percentage = Math.round(
          (tooltipItem.raw /
            tooltipItem.chart.$totalizer.totals[tooltipItem.dataIndex]) *
            100,
        );
        return `${label}: ${value} (${percentage}%)`;
      }

      // For all other charts, show only label and value
      return `${label}: ${value}`;
    };

    const options = {
      maintainAspectRatio: false,
      aspectRatio: 1,
      indexAxis: indexAxis,
      elements: {
        bar: {
          borderRadius: borderRadius,
        },
      },
      plugins: {
        title: {
          display: !!title,
          text: title,
          font: {
            size: 16,
            weight: '400',
            family: i18n.language === 'ar' ? 'DI NEXT ARABIC' : 'Myriad Pro',
          },
        },
        datalabels: {
          color: 'black',
          display: true,
          anchor: 'end',
          align: 'top',
          formatter: function (value, context) {
            return value;
          },
        },
        tooltip: {
          mode: 'index',
          intersect: true,
          callbacks: {
            label: afterLabel,
            footer: footer,
          },
        },
        legend: {
          display: showLegend,
          position: 'bottom',
          align: 'start',
          labels: {
            usePointStyle: true,
            color: textColor,
          },
        },
      },
      scales: {
        x: isTimeseries
          ? {
              type: 'category',
              stacked: true,
              ticks: {
                color: textColorSecondary,
              },
              grid: {
                color: surfaceBorder,
                drawTicks: false,
              },
              border: {
                dash: [2, 2],
                display: false,
              },
            }
          : {
              stacked: true,
              ticks: {
                color: textColorSecondary,
              },
              grid: {
                color: surfaceBorder,
                drawTicks: false,
              },
              border: {
                dash: [2, 2],
                display: false,
              },
            },
        y: {
          stacked: true,
          grace: '30%',
          border: {
            color: 'white',
            dash: [2, 2],
          },
          ticks: {
            color: textColorSecondary,
          },
          grid: {
            drawTicks: false,
            color: surfaceBorder,
          },
        },
      },
    };

    setChartData(data);
    setChartOptions(options);
  }, [
    labels,
    dataset,
    isTimeseries,
    dateFrom,
    dateTo,
    periodType,
    showLegend,
    indexAxis,
    borderRadius,
    title,
    chartType,
    vatData,
  ]);

  return (
    <Chart
      type="bar"
      data={chartData}
      options={chartOptions}
      plugins={[ChartDataLabels, totalizer]}
    />
  );
};
